import { create } from "zustand";
import { trackEvent } from "../services/analyticsService";

export type ChatViewMode = "collapsed" | "sidebar" | "fullscreen";

export type AppRoute = "welcome" | "chat" | "documents" | "prompts" | "mindspace";

export interface RouteParams {
  sessionId?: string;
}

interface UIState {
  chatViewMode: ChatViewMode;
  isHistoryPanelVisibleOnMobile: boolean;
  isInitializingChat: boolean;
  hasTrackedSessionOpen: boolean;
  // Lightweight client-side routing for fullpage and widget fullscreen
  route: AppRoute;
  params?: RouteParams;
  // Fullpage shell mobile sidebar state
  isSidebarOpenOnMobile: boolean;
  setChatViewMode: (mode: ChatViewMode) => void;
  toggleHistoryPanelVisibility: () => void;
  setIsInitializingChat: (isInitializing: boolean) => void;
  // Potentially, add functions to open to specific views directly
  openChatToSidebar: () => void;
  openChatToFullscreen: () => void;
  closeChatToBubble: () => void;
  minimizeChatToSidebar: () => void; // If fullscreen can go back to sidebar
  navigate: (route: AppRoute, params?: RouteParams) => void;
  openSidebarMobile: () => void;
  closeSidebarMobile: () => void;
  toggleSidebarMobile: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  chatViewMode: "collapsed", // Initial state
  isHistoryPanelVisibleOnMobile: false, // Initial state
  isInitializingChat: false,
  hasTrackedSessionOpen: false,
  route: "welcome",
  params: undefined,
  isSidebarOpenOnMobile: false,
  setChatViewMode: (mode) => set({ chatViewMode: mode }),
  toggleHistoryPanelVisibility: () =>
    set((state) => ({
      isHistoryPanelVisibleOnMobile: !state.isHistoryPanelVisibleOnMobile,
    })),
  setIsInitializingChat: (isInitializing) =>
    set({ isInitializingChat: isInitializing }),
  openChatToSidebar: () => {
    if (!get().hasTrackedSessionOpen) {
      trackEvent("widget_opened");
      set({ hasTrackedSessionOpen: true });
    }
    set({ chatViewMode: "sidebar", isHistoryPanelVisibleOnMobile: false });
  },
  openChatToFullscreen: () => set({ chatViewMode: "fullscreen" }),
  closeChatToBubble: () =>
    set({ chatViewMode: "collapsed", isHistoryPanelVisibleOnMobile: false }),
  minimizeChatToSidebar: () =>
    set({ chatViewMode: "sidebar", isHistoryPanelVisibleOnMobile: false }),
  navigate: (route, params) => {
    set({ route, params });
  },
  openSidebarMobile: () => set({ isSidebarOpenOnMobile: true }),
  closeSidebarMobile: () => set({ isSidebarOpenOnMobile: false }),
  toggleSidebarMobile: () =>
    set((s) => ({ isSidebarOpenOnMobile: !s.isSidebarOpenOnMobile })),
}));
